import { createTheme } from '@mui/material/styles';

// Color palette based on provided hex codes
const colorPalette = {
    olive: '#A0AB60',
    golden: '#EABB51',
    navy: '#0B274D',
    orange: '#D46520',
    beige: '#DEC8AD',
    red: '#FF2828',
    // Additional colors for theme support
    lightGray: '#F5F5F5',
    darkGray: '#1A1A1A',
    mediumGray: '#333333',
    white: '#FFFFFF',
    black: '#000000',
};

// Light theme configuration
export const lightTheme = createTheme({
    palette: {
        mode: 'light',
        primary: {
            main: colorPalette.olive,
            light: '#B8C47D',
            dark: '#7A8348',
            contrastText: '#FFFFFF',
        },
        secondary: {
            main: colorPalette.navy,
            light: '#2B4970',
            dark: '#051732',
            contrastText: '#FFFFFF',
        },
        error: {
            main: colorPalette.red,
            light: '#FF5555',
            dark: '#CC1F1F',
            contrastText: '#FFFFFF',
        },
        warning: {
            main: colorPalette.orange,
            light: '#DD8147',
            dark: '#B04D0A',
            contrastText: '#FFFFFF',
        },
        info: {
            main: colorPalette.golden,
            light: '#EDC671',
            dark: '#D1A241',
            contrastText: colorPalette.navy,
        },
        success: {
            main: colorPalette.olive,
            light: '#B8C47D',
            dark: '#7A8348',
            contrastText: '#FFFFFF',
        },
        background: {
            default: '#F8F9FA',
            paper: '#FFFFFF',
        },
        text: {
            primary: '#1A1A1A',
            secondary: '#4A4A4A',
        },
        divider: '#E8E8E8',
        action: {
            hover: 'rgba(160, 171, 96, 0.04)',
            selected: 'rgba(160, 171, 96, 0.08)',
        },
    },
    typography: {
        fontFamily: '"League Spartan", "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
        fontSize: 16,
        h1: {
            fontFamily: '"League Spartan", "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 700,
            fontSize: '2.75rem',
            lineHeight: 1.2,
            letterSpacing: '-0.02em',
        },
        h2: {
            fontWeight: 700,
            fontSize: '2.25rem',
            lineHeight: 1.25,
            letterSpacing: '-0.01em',
        },
        h3: {
            fontWeight: 600,
            fontSize: '1.875rem',
            lineHeight: 1.3,
            letterSpacing: '-0.005em',
        },
        h4: {
            fontWeight: 600,
            fontSize: '1.5rem',
            lineHeight: 1.35,
        },
        h5: {
            fontWeight: 600,
            fontSize: '1.375rem',
            lineHeight: 1.4,
        },
        h6: {
            fontWeight: 600,
            fontSize: '1.25rem',
            lineHeight: 1.45,
        },
        subtitle1: {
            fontSize: '1.125rem',
            fontWeight: 500,
            lineHeight: 1.5,
        },
        subtitle2: {
            fontSize: '1rem',
            fontWeight: 500,
            lineHeight: 1.45,
        },
        body1: {
            fontSize: '1.125rem',
            lineHeight: 1.65,
            fontWeight: 400,
        },
        body2: {
            fontSize: '1rem',
            lineHeight: 1.6,
            fontWeight: 400,
        },
        button: {
            fontSize: '1rem',
            fontWeight: 600,
            lineHeight: 1.4,
            textTransform: 'none',
            letterSpacing: '0.01em',
        },
        caption: {
            fontSize: '0.875rem',
            lineHeight: 1.5,
            fontWeight: 400,
        },
        overline: {
            fontSize: '0.75rem',
            fontWeight: 600,
            lineHeight: 1.4,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
        },
    },
    spacing: 8,
    components: {
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: 'transparent',
                    color: '#1A1A1A',
                    boxShadow: 'none',
                    borderBottom: 'none',
                    backdropFilter: 'none',
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    backgroundColor: '#FFFFFF',
                    borderRight: '1px solid rgba(0, 0, 0, 0.08)',
                    boxShadow: '4px 0 16px rgba(0, 0, 0, 0.08)',
                    color: '#1A1A1A',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                    padding: '12px 20px',
                    minHeight: 44,
                    letterSpacing: '0.01em',
                },
                contained: {
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    fontWeight: 600,
                    '&:hover': {
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                    },
                },
                outlined: {
                    fontWeight: 500,
                },
                text: {
                    fontWeight: 500,
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #F0F0F0',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                    '&:hover': {
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                },
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    margin: '2px 8px',
                    padding: '12px 16px',
                    color: '#4A4A4A',
                    '&:hover': {
                        backgroundColor: 'rgba(160, 171, 96, 0.08)',
                        color: '#1A1A1A',
                    },
                    '&.Mui-selected': {
                        backgroundColor: 'rgba(160, 171, 96, 0.12)',
                        color: colorPalette.olive,
                        '&:hover': {
                            backgroundColor: 'rgba(160, 171, 96, 0.16)',
                        },
                        '& .MuiListItemIcon-root': {
                            color: colorPalette.olive,
                        },
                        '& .MuiListItemText-primary': {
                            color: colorPalette.olive,
                            fontWeight: 600,
                        },
                    },
                    '& .MuiListItemIcon-root': {
                        color: '#4A4A4A',
                    },
                },
            },
        },
        MuiListItemText: {
            styleOverrides: {
                primary: {
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: 'inherit',
                    lineHeight: 1.4,
                },
                secondary: {
                    fontSize: '0.875rem',
                    fontWeight: 400,
                    color: '#666666',
                    lineHeight: 1.4,
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    height: 32,
                    '&.MuiChip-outlined': {
                        borderColor: '#E8E8E8',
                        color: '#1A1A1A',
                        fontWeight: 500,
                    },
                },
                label: {
                    paddingLeft: 12,
                    paddingRight: 12,
                },
            },
        },
        MuiToolbar: {
            styleOverrides: {
                root: {
                    minHeight: '56px',
                    padding: '0 16px',
                    backgroundColor: 'transparent',
                    '@media (min-width: 600px)': {
                        minHeight: '56px',
                        padding: '0 24px',
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    fontSize: '0.95rem',
                    fontWeight: 400,
                    lineHeight: 1.5,
                },
                head: {
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    lineHeight: 1.4,
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&:nth-of-type(even)': {
                        backgroundColor: 'rgba(160, 171, 96, 0.03)',
                    },
                    '&:hover': {
                        backgroundColor: 'rgba(160, 171, 96, 0.08)',
                    },
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    fontSize: '1rem',
                    fontWeight: 400,
                    lineHeight: 1.5,
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    lineHeight: 1.4,
                },
            },
        },
    },
});

// Dark theme configuration
export const darkTheme = createTheme({
    palette: {
        mode: 'dark',
        primary: {
            main: colorPalette.olive,
            light: '#B8C47D',
            dark: '#7A8348',
            contrastText: '#FFFFFF',
        },
        secondary: {
            main: colorPalette.golden,
            light: '#EDC671',
            dark: '#D1A241',
            contrastText: '#1A1A1A',
        },
        error: {
            main: colorPalette.red,
            light: '#FF5555',
            dark: '#CC1F1F',
            contrastText: '#FFFFFF',
        },
        warning: {
            main: colorPalette.orange,
            light: '#DD8147',
            dark: '#B04D0A',
            contrastText: '#FFFFFF',
        },
        info: {
            main: colorPalette.golden,
            light: '#EDC671',
            dark: '#D1A241',
            contrastText: '#1A1A1A',
        },
        success: {
            main: colorPalette.olive,
            light: '#B8C47D',
            dark: '#7A8348',
            contrastText: '#FFFFFF',
        },
        background: {
            default: '#0F0F0F',
            paper: '#1E1E1E',
        },
        text: {
            primary: '#FFFFFF',
            secondary: '#C0C0C0',
        },
        divider: '#2A2A2A',
        action: {
            hover: 'rgba(160, 171, 96, 0.08)',
            selected: 'rgba(160, 171, 96, 0.12)',
        },
    },
    typography: {
        fontFamily: '"League Spartan", "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
        fontSize: 16,
        h1: {
            fontFamily: '"League Spartan", "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 700,
            fontSize: '2.75rem',
            lineHeight: 1.2,
            letterSpacing: '-0.02em',
        },
        h2: {
            fontWeight: 700,
            fontSize: '2.25rem',
            lineHeight: 1.25,
            letterSpacing: '-0.01em',
        },
        h3: {
            fontWeight: 600,
            fontSize: '1.875rem',
            lineHeight: 1.3,
            letterSpacing: '-0.005em',
        },
        h4: {
            fontWeight: 600,
            fontSize: '1.5rem',
            lineHeight: 1.35,
        },
        h5: {
            fontWeight: 600,
            fontSize: '1.375rem',
            lineHeight: 1.4,
        },
        h6: {
            fontWeight: 600,
            fontSize: '1.25rem',
            lineHeight: 1.45,
        },
        subtitle1: {
            fontSize: '1.125rem',
            fontWeight: 500,
            lineHeight: 1.5,
        },
        subtitle2: {
            fontSize: '1rem',
            fontWeight: 500,
            lineHeight: 1.45,
        },
        body1: {
            fontSize: '1.125rem',
            lineHeight: 1.65,
            fontWeight: 400,
        },
        body2: {
            fontSize: '1rem',
            lineHeight: 1.6,
            fontWeight: 400,
        },
        button: {
            fontSize: '1rem',
            fontWeight: 600,
            lineHeight: 1.4,
            textTransform: 'none',
            letterSpacing: '0.01em',
        },
        caption: {
            fontSize: '0.875rem',
            lineHeight: 1.5,
            fontWeight: 400,
        },
        overline: {
            fontSize: '0.75rem',
            fontWeight: 600,
            lineHeight: 1.4,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
        },
    },
    spacing: 8,
    components: {
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: 'transparent',
                    color: '#FFFFFF',
                    boxShadow: 'none',
                    borderBottom: 'none',
                    backdropFilter: 'none',
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    backgroundColor: '#242424',
                    borderRight: 'none',
                    boxShadow: '4px 0 16px rgba(0, 0, 0, 0.3)',
                    color: '#FFFFFF',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                    padding: '12px 20px',
                    minHeight: 44,
                    letterSpacing: '0.01em',
                },
                contained: {
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                    fontWeight: 600,
                    '&:hover': {
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.4)',
                    },
                },
                outlined: {
                    fontWeight: 500,
                },
                text: {
                    fontWeight: 500,
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    backgroundColor: '#1E1E1E',
                    border: '1px solid #2A2A2A',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)',
                    '&:hover': {
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundColor: '#1E1E1E',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)',
                },
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    margin: '2px 8px',
                    padding: '12px 16px',
                    color: 'rgba(255, 255, 255, 0.9)',
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: '#FFFFFF',
                    },
                    '&.Mui-selected': {
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        color: '#FFFFFF',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.25)',
                        },
                        '& .MuiListItemIcon-root': {
                            color: '#FFFFFF',
                        },
                        '& .MuiListItemText-primary': {
                            color: '#FFFFFF',
                            fontWeight: 600,
                        },
                    },
                    '& .MuiListItemIcon-root': {
                        color: 'rgba(255, 255, 255, 0.9)',
                    },
                },
            },
        },
        MuiListItemText: {
            styleOverrides: {
                primary: {
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: 'inherit',
                    lineHeight: 1.4,
                },
                secondary: {
                    fontSize: '0.875rem',
                    fontWeight: 400,
                    lineHeight: 1.4,
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    height: 32,
                    '&.MuiChip-outlined': {
                        borderColor: '#2A2A2A',
                        color: '#FFFFFF',
                        fontWeight: 500,
                    },
                },
                label: {
                    paddingLeft: 12,
                    paddingRight: 12,
                },
            },
        },
        MuiToolbar: {
            styleOverrides: {
                root: {
                    minHeight: '56px',
                    padding: '0 16px',
                    backgroundColor: 'transparent',
                    '@media (min-width: 600px)': {
                        minHeight: '56px',
                        padding: '0 24px',
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    fontSize: '0.95rem',
                    fontWeight: 400,
                    lineHeight: 1.5,
                },
                head: {
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    lineHeight: 1.4,
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&:nth-of-type(even)': {
                        backgroundColor: 'rgba(255, 255, 255, 0.02)',
                    },
                    '&:hover': {
                        backgroundColor: 'rgba(160, 171, 96, 0.08)',
                    },
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    fontSize: '1rem',
                    fontWeight: 400,
                    lineHeight: 1.5,
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    lineHeight: 1.4,
                },
            },
        },
    },
});

export { colorPalette }; 